import { Link } from "react-router-dom";
import { ChevronRight, DollarSign, ShieldCheck, Car, CalendarCheck, Percent, MapPin, Wrench, Plane, Gauge, Zap, Users, Truck, Snowflake, Star, FileText, AlertTriangle, MessageCircle, Receipt, Ban, TrendingUp, Sparkles, PawPrint, Rocket } from "lucide-react";
import { Seo } from "@/components/Seo";
import { HOST_GUIDES, HOST_GUIDE_SLUGS, GUIDE_CATEGORIES } from "@/data/hostGuides";

const SITE = "https://belorentals.com";
const ICONS = { DollarSign, ShieldCheck, Car, CalendarCheck, Percent, MapPin, Wrench, Plane, Gauge, Zap, Users, Truck, Snowflake, Star, FileText, AlertTriangle, MessageCircle, Receipt, Ban, TrendingUp, Sparkles, PawPrint, Rocket };

export default function HostGuidesHub() {
  const itemListLd = { "@context": "https://schema.org", "@type": "ItemList", itemListElement: HOST_GUIDE_SLUGS.map((s, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE}/host-guides/${s}`, name: HOST_GUIDES[s].h1 })) };
  const crumbLd = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Host Guides", item: `${SITE}/host-guides` },
  ] };

  return (
    <div data-testid="host-guides-hub" className="bg-white">
      <Seo
        title="Car Hosting Guides for Massachusetts Hosts | Belo Rentals"
        description="Practical guides and how-tos for Massachusetts car rental hosts: pricing, insurance responsibility, listing photos, guest handoff, cleaning, payouts, and getting more bookings."
        path="/host-guides"
        keywords="car hosting guides, how to rent out your car, car sharing host tips Massachusetts"
        jsonLd={[itemListLd, crumbLd]}
      />

      <section className="relative pt-32 pb-14 px-5 sm:px-8 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.22),transparent)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/15 text-blue-200 text-sm font-medium mb-6">Host resources</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Car Hosting Guides</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">Practical, Massachusetts-focused how-tos for pricing, insurance, photos, handoffs, and getting more bookings on Belo Rentals.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 space-y-14">
        {GUIDE_CATEGORIES.map((cat) => {
          const slugs = HOST_GUIDE_SLUGS.filter((s) => HOST_GUIDES[s].category === cat.key);
          if (!slugs.length) return null;
          return (
            <section key={cat.key}>
              <h2 className="font-heading text-2xl font-bold text-slate-900">{cat.label}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
                {slugs.map((s) => {
                  const g = HOST_GUIDES[s];
                  const Ic = ICONS[g.icon] || Car;
                  return (
                    <Link key={s} to={`/host-guides/${s}`} data-testid={`guide-card-${s}`} className="group bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-300 hover:shadow-sm transition-all">
                      <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4"><Ic size={20} /></span>
                      <h3 className="font-heading font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{g.h1}</h3>
                      <p className="text-slate-600 text-sm mt-2 leading-relaxed">{g.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-blue-600 text-sm font-semibold">Read guide <ChevronRight size={15} /></span>
                    </Link>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
